import { fromJson, toJson, create } from "@bufbuild/protobuf";

import { pb } from "@uic/adapters/pocketbase";
import {
  SignupRequestSchema,
  SignupResponseSchema,
} from "@uic/adapters/slorgproto";
import { setBackendType } from "@uic/domain/backend";
import { wsc } from "@uic/domain/wsc";

/**
 * @typedef {import('./types').User} User
 */

/**
 * @param {string} email The email of the new user
 * @param {string} name The name of the new user
 * @param {string} password The password of the new user
 * @returns {string} the new user
 */
export async function passwordSignup(email, name, password) {
  const req = create(SignupRequestSchema, { email, password, name });
  const response = await wsc.post("/signup", toJson(SignupRequestSchema, req));
  const proto = fromJson(SignupResponseSchema, response.data);
  return proto.userId;
}

/**
 * @param {string} email The email of the user
 * @param {string} password The password of the user
 * @returns {object} The pocketbase auth data
 */
export async function passwordSignin(email, password) {
  const authData = await pb
    .collection("users")
    .authWithPassword(email, password);
  return {
    id: authData.record.id,
    email: authData.record.email,
    name: authData.record.name,
    token: authData.token,
    created: authData.record.created,
  };
}

/**
 * Logs out the current user.
 *
 * The storage backend goes with the token, or the next sign-in would read the
 * previous user's backend until its own config lands.
 */
export function signout() {
  pb.authStore.clear();
  setBackendType(null);
}

/**
 * @returns {User} The user if there, otherwise throw an error
 */
export function getUser() {
  if (pb.authStore.isValid) {
    const authStore = pb.authStore;
    return {
      id: authStore.record.id,
      email: authStore.record.email,
      name: authStore.record.name,
      token: authStore.token,
      created: authStore.record.created,
    };
  } else {
    throw new Error("user is not logged in");
  }
}

/**
 * Keep the session going! Called on first page load if they're signed in.
 * @returns {User} The refreshed user with a new token
 */
export async function refreshToken() {
  await pb.collection("users").authRefresh();

  return getUser();
}
